// @givmo/design-system — Tabs (Base UI Tabs).
//
// 03-3c overlay tier. Ports the Flutter webapp `CTabBar` (app_bars/tab_bar.dart): a horizontal
// tab strip with a sliding 2px blue underline indicator (radius all4), a 1px gray60 divider
// under the whole strip, blue20 hover/focus overlay, and gray text (selected = darker/bolder).
// Built on @base-ui/react/tabs, which wires role="tablist"/"tab"/"tabpanel", `aria-selected`,
// `aria-controls`/`aria-labelledby` association, roving tabindex, and arrow-key navigation.
//
// The sliding indicator uses Base UI's <Tabs.Indicator> CSS vars (`--active-tab-left` /
// `--active-tab-width`) so it animates between tabs without per-tab borders.
//
// A strip never wraps a label and never widens its page (CBG-DS2). Each label stays on one line, and
// a strip wider than its container scrolls sideways inside its own lane (the list), keeping the
// active tab in view. Where every tab fits, the lane is inert. Because the lane clips anything drawn
// outside it, the SaaS tab's focus ring sits inside the tab.
//
// a11y: roles + keyboard nav are Base UI's; `testId` forwards to the root. Tab/Panel accept
// their own `testId`. The active Panel is a focus stop (Base UI sets `tabIndex={0}`), so it ships
// the house focus ring by default — see `INSET_FOCUS_RING`.
import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { cn } from '../lib/utils';
import { INSET_FOCUS_RING } from '../lib/focus';
import { TAB_LANE, revealInLane } from '../lib/lane';
import { testIdProp, type TestIdProps } from '../lib/testid';

export type TabsValue = string | number;

/** Visual appearance for the Tabs family (added 2026-06-26, ADDITIVE — mirrors Button/Select). */
export type TabsAppearance = 'legacy' | 'saas';

// The appearance is set on <Tabs.Root> and read by List/Tab via context so callers only specify it
// once. Defaults to `legacy` so donate/webapp consumers are unchanged.
const TabsAppearanceContext = createContext<TabsAppearance>('legacy');

export interface TabsRootProps extends TestIdProps {
  children: ReactNode;
  /** Controlled active tab value. */
  value?: TabsValue;
  /** Uncontrolled initial active tab value. */
  defaultValue?: TabsValue;
  onValueChange?: (value: TabsValue) => void;
  className?: string;
  /**
   * `legacy` (default) → the Flutter-ported `CTabBar` (35px inline padding, s20, blue underline).
   * `saas`             → a clean fintech tablist (tight underline tabs, brand-600 indicator).
   */
  appearance?: TabsAppearance;
}

function TabsRoot({
  children,
  value,
  defaultValue,
  onValueChange,
  className,
  testId,
  appearance = 'legacy',
}: TabsRootProps) {
  return (
    <TabsAppearanceContext value={appearance}>
      <BaseTabs.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={(next) => onValueChange?.(next as TabsValue)}
        // `min-w-0`: as a row-flex or grid item, the root may shrink below its strip's one-line
        // width, so the lane scrolls rather than the root pushing its container wider.
        className={cn('flex min-w-0 flex-col', className)}
        {...testIdProp(testId)}
      >
        {children}
      </BaseTabs.Root>
    </TabsAppearanceContext>
  );
}

export interface TabsListProps extends TestIdProps {
  children: ReactNode;
  className?: string;
  'aria-label'?: string;
}

function TabsList({ children, className, testId, 'aria-label': ariaLabel }: TabsListProps) {
  const saas = useContext(TabsAppearanceContext) === 'saas';
  const laneRef = useRef<HTMLDivElement>(null);

  // The list is the strip's lane (`TAB_LANE`), and a lane can leave the ACTIVE tab (and its
  // underline) outside its visible window. Whenever Base UI moves `data-active`, and on mount, bring
  // the active tab whole into view. Where the strip fits, this does nothing. Base UI positions the
  // Indicator in the list's scroll coordinates, so the underline travels with its tab.
  useEffect(() => {
    const lane = laneRef.current;
    if (lane === null) return undefined;
    const reveal = () => {
      const active = lane.querySelector('[role="tab"][data-active]');
      if (active !== null) revealInLane(lane, active);
    };
    reveal();
    const observer = new MutationObserver(reveal);
    observer.observe(lane, { subtree: true, attributeFilter: ['data-active'] });
    return () => observer.disconnect();
  }, []);

  return (
    <BaseTabs.List
      ref={laneRef}
      aria-label={ariaLabel}
      className={cn(
        saas
          ? // SaaS: a clean divider with tighter tab spacing.
            'relative flex gap-24 border-b border-border'
          : // 1px gray60 divider under the whole strip (Flutter dividerColor gray60, height 1).
            'relative flex border-b border-gray60',
        TAB_LANE,
        className,
      )}
      {...testIdProp(testId)}
    >
      {children}
      {/* Sliding underline indicator (Base UI CSS vars drive the slide). */}
      <BaseTabs.Indicator
        className={
          saas
            ? cn(
                'absolute bottom-0 left-[var(--active-tab-left)] h-2 w-[var(--active-tab-width)]',
                'rounded-full bg-brand-600 transition-all duration-200',
              )
            : cn(
                'absolute bottom-0 left-[var(--active-tab-left)] h-2 w-[var(--active-tab-width)]',
                'rounded-all4 bg-blue transition-all duration-200',
              )
        }
      />
    </BaseTabs.List>
  );
}

export interface TabsTabProps extends TestIdProps {
  children: ReactNode;
  value: TabsValue;
  disabled?: boolean;
  className?: string;
}

/**
 * One tab. Both appearances style the selected label on `data-active`, the attribute Base UI's Tab
 * sets on the active tab (`TabsTabDataAttributes.active`, 1.6.0). It sets no `data-selected`: that
 * one is Select.Item's (`select.tsx`). A variant keyed on an attribute nothing sets matches nothing
 * and fails silently: keyed on `data-selected` from the June port until CBG-DS1, no tab strip ever
 * rendered its selected label strong. `tabs.test.tsx` checks that the selected styling is keyed on
 * an attribute the active tab actually carries.
 */
function TabsTab({ children, value, disabled, className, testId }: TabsTabProps) {
  const saas = useContext(TabsAppearanceContext) === 'saas';
  return (
    <BaseTabs.Tab
      value={value}
      disabled={disabled}
      className={
        saas
          ? cn(
              'relative rounded-t-md px-4 py-10 font-sans text-s14 font-w500 tracking-normal outline-none transition-colors',
              // One line, never squeezed: the lane scrolls instead (CBG-DS2).
              'shrink-0 whitespace-nowrap',
              // Idle tertiary text → strong on hover; selected = strong text (the brand underline
              // is the Indicator).
              'text-text-tertiary hover:text-text',
              'data-[active]:font-w600 data-[active]:text-text-strong',
              // Focus drawn inside: the lane would cut an outer ring's top and bottom.
              INSET_FOCUS_RING,
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:text-text-tertiary',
              className,
            )
          : cn(
              'relative px-[35px] py-12 font-sans text-s20 tracking-givmo outline-none',
              // One line, never squeezed: the lane scrolls instead (CBG-DS2).
              'shrink-0 whitespace-nowrap',
              // Unselected = gray w400; selected = gray w600 (Flutter labelStyle vs unselectedLabelStyle).
              'font-w400 text-gray',
              'data-[active]:font-w600',
              // blue20 hover/focus overlay (Flutter overlayColor blue20).
              'hover:bg-blue20 focus-visible:bg-blue20',
              // Base UI renders disabled tabs via aria-disabled/data-disabled, not the native attr.
              'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[disabled]:hover:bg-transparent',
              className,
            )
      }
      {...testIdProp(testId)}
    >
      {children}
    </BaseTabs.Tab>
  );
}

export interface TabsPanelProps extends TestIdProps {
  children: ReactNode;
  value: TabsValue;
  /** Keep the panel mounted while inactive (default false). */
  keepMounted?: boolean;
  className?: string;
  /**
   * Whether the active panel is itself a tab stop. Default true: Base UI sets `tabIndex={0}`, so a
   * panel with no focusable child is still reachable by keyboard. Pass `false` when the panel always
   * contains focusable content — per the WAI-ARIA APG a tabpanel is focusable only when nothing
   * inside it is, and a `tabindex="0"` panel wrapped around focusable content is a dead stop in the
   * tab order.
   */
  focusable?: boolean;
}

/**
 * The active panel is a focus stop: Base UI gives it `tabIndex={0}` on purpose
 * (`tabs/panel/TabsPanel.js`), so a keyboard user can reach panel content that has no other focusable
 * child. A focus stop owes a visible indicator (WCAG SC 2.4.7), so the panel wears the house outline,
 * drawn inside (`INSET_FOCUS_RING`): a panel is a full-width region, and an outset outline would be
 * clipped by the surrounding card. The fix is the pairing, never removing `tabIndex`. `outline-none`
 * is UNMODIFIED and the outline is `focus-visible:`-modified, so tailwind-merge keeps both, and a call
 * site can still override any single outline property (style, width, offset and colour are each their
 * own conflict group).
 */
function TabsPanel({ children, value, keepMounted, className, testId, focusable }: TabsPanelProps) {
  return (
    <BaseTabs.Panel
      value={value}
      keepMounted={keepMounted}
      // Spread, not `tabIndex={undefined}`: an explicit undefined would override Base UI's own
      // `tabIndex={0}` and silently drop the default tab stop.
      {...(focusable === false ? { tabIndex: -1 } : {})}
      className={cn('pt-16 outline-none', INSET_FOCUS_RING, className)}
      {...testIdProp(testId)}
    >
      {children}
    </BaseTabs.Panel>
  );
}

/** Compound Tabs API: `<Tabs.Root>…<Tabs.List>…<Tabs.Tab>…<Tabs.Panel>`. */
export const Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Tab: TabsTab,
  Panel: TabsPanel,
};
